import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Clock } from "lucide-react";
import { api, shopDateTime } from "@/lib/api";
import { formatPhone } from "@/lib/phone";
import { openIdRequest } from "@/lib/idRequest";

export const WaitingOnCustomer = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api
      .get("/kyc-outstanding")
      .then(({ data }) => setRows(data))
      .catch(() => toast.error("Could not load the outstanding ID requests"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || rows.length === 0) return null;

  return (
    <div className="mt-6 border border-[#9A3412] bg-[#FEF6F2] p-5" data-testid="waiting-on-customer">
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-[#9A3412]" />
        <h2 className="text-base font-bold md:text-lg">
          Waiting on customer ({rows.length})
        </h2>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        ID asked for and not yet received, or a form you rejected that has not been replaced.
      </p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm" data-testid="waiting-table">
          <thead className="bg-[#0A0A0A] text-white">
            <tr>
              {["Customer", "Phone", "Store", "Why", "Last asked", "Ask again"].map((h) => (
                <th key={h} className="px-3 py-2.5 text-left font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-white">
            {rows.map((r) => (
              <tr key={r.phone + r.kyc_id} data-testid={`waiting-row-${r.phone}`}>
                <td className="px-3 py-2.5 font-semibold">{r.customer_name || "-"}</td>
                <td className="tabular px-3 py-2.5">{formatPhone(r.phone)}</td>
                <td className="px-3 py-2.5 text-xs uppercase tracking-wider">{r.branch}</td>
                <td className="px-3 py-2.5 text-xs">
                  <span className="font-bold uppercase text-[#9A3412]">{r.reason}</span>
                  {r.review_reason ? ` — ${r.review_reason}` : ""}
                </td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground">
                  {r.last_request_at ? shopDateTime(r.last_request_at) : "not asked yet"}
                  {r.last_request_by ? <div>by {r.last_request_by}</div> : null}
                </td>
                <td className="px-3 py-2.5">
                  <button
                    onClick={() =>
                      openIdRequest({
                        phone: r.phone,
                        customer_name: r.customer_name,
                        branch: r.branch,
                        reason: r.review_reason,
                        kyc_id: r.kyc_id,
                      }).then(load)
                    }
                    className="flex items-center gap-1.5 border border-[#1F6F43] px-2.5 py-1.5 text-xs font-semibold text-[#1F6F43] transition-colors duration-150 hover:bg-[#F1FAF4]"
                    data-testid={`waiting-ask-again-${r.phone}`}
                  >
                    <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
